import React from 'react';
import { Phone, MapPin, Clock, CheckCircle } from 'lucide-react';
import { Card } from './ui/card';
import { psychologistData } from '../mock';

const ContactSection = () => {
  return (
    <section id="contact" className="py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-6">
            Get In Touch
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Ready to take the first step towards better mental health? 
            Contact us today for a free consultation or to book an appointment.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-12">
          {/* Contact Information */}
          <div className="space-y-8">
            <Card className="p-8 bg-white shadow-lg border-0">
              <h3 className="text-2xl font-bold text-soft-blue mb-6">Contact Information</h3>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-sage-green rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone className="text-white" size={20} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-charcoal-grey">Phone & WhatsApp</h4>
                    <p className="text-charcoal-grey">{psychologistData.contact.phone}</p>
                    <p className="text-sm text-charcoal-grey opacity-75">Available 9 AM - 8 PM</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-green-600" size={20} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Locations</h4>
                    <div className="space-y-2">
                      {psychologistData.contact.locations.map((location, index) => (
                        <p key={index} className="text-gray-600 text-sm">{location}</p>
                      ))}
                    </div>
                    <div className="mt-2 space-y-1">
                      <p className="text-xs text-gray-500">{psychologistData.contact.schedules.weekdays}</p>
                      <p className="text-xs text-gray-500">{psychologistData.contact.schedules.sunday}</p>
                    </div>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Clock className="text-blue-600" size={20} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Online Sessions</h4>
                    <div className="text-gray-600 text-sm space-y-1">
                      <p>Available daily for flexible access</p>
                      <p className="text-xs text-gray-500">Free 30-minute online consultation</p>
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            {/* Special Offer */}
            <Card className="p-6 bg-gradient-to-r from-green-600 to-blue-600 text-white border-0">
              <div className="text-center space-y-4">
                <CheckCircle className="mx-auto text-green-200" size={48} />
                <h4 className="text-xl font-bold">{psychologistData.offer.title}</h4>
                <p className="text-green-100 text-sm">{psychologistData.offer.description}</p>
              </div>
            </Card>
          </div>

          {/* WhatsApp Contact & Additional Info */}
          <div className="lg:col-span-2 space-y-8">
            {/* WhatsApp Button */}
            <Card className="p-12 bg-white shadow-lg border-0 text-center">
              <h3 className="text-2xl font-bold text-sage-green mb-6">Ready to Start Your Journey?</h3>
              <p className="text-charcoal-grey mb-8 leading-relaxed">
                Ready to take the next step? Contact me directly on WhatsApp to schedule your consultation 
                or discuss your needs.
              </p>
              <a 
                href="https://wa.me/919167250014?text=Hi%20Manish,%20I'd%20like%20to%20schedule%20a%20consultation" 
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center bg-sage-green hover:bg-soft-blue text-white px-8 py-4 rounded-full font-semibold text-lg transition-all duration-200 hover:shadow-lg hover:scale-105 w-full max-w-md mx-auto"
              >
                <Phone className="mr-3" size={24} />
                📱 Contact via WhatsApp
              </a>
              <p className="text-sm text-charcoal-grey opacity-75 mt-4">
                Click to open WhatsApp and send a message directly
              </p>
            </Card>

            {/* Additional Information Cards */}
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="p-6 bg-gradient-to-br from-blue-50 to-white border-0 shadow-lg">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">Quick Response</h4>
                <p className="text-gray-600 text-sm">
                  I typically respond to messages within a few hours during business hours. 
                  For urgent matters, please call directly.
                </p>
              </Card>

              <Card className="p-6 bg-gradient-to-br from-green-50 to-white border-0 shadow-lg">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">Flexible Scheduling</h4>
                <p className="text-gray-600 text-sm">
                  Both in-person and online sessions available. 
                  Evening and weekend appointments can be arranged upon request.
                </p>
              </Card>
            </div>

            {/* How to Prepare Card */}
            <Card className="p-8 bg-gradient-to-r from-gray-50 to-blue-50 border-0 shadow-lg">
              <h4 className="text-xl font-semibold text-gray-900 mb-4 text-center">How to Prepare for Your Session</h4>
              <div className="grid md:grid-cols-3 gap-6 text-center">
                <div className="space-y-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
                    <span className="text-blue-600 font-bold text-lg">1</span>
                  </div>
                  <h5 className="font-medium text-gray-900">Think About Goals</h5>
                  <p className="text-sm text-gray-600">Consider what you hope to achieve from therapy</p>
                </div>
                <div className="space-y-3">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                    <span className="text-green-600 font-bold text-lg">2</span>
                  </div>
                  <h5 className="font-medium text-gray-900">Prepare Questions</h5>
                  <p className="text-sm text-gray-600">Write down any questions or concerns you'd like to discuss</p>
                </div>
                <div className="space-y-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
                    <span className="text-blue-600 font-bold text-lg">3</span>
                  </div>
                  <h5 className="font-medium text-gray-900">Be Open</h5>
                  <p className="text-sm text-gray-600">Come with an open mind and willingness to share</p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;